import styled, { css } from 'styled-components/macro';

import BaseTable from 'components/data-visualization/Table/Table';
import ColumnHeader from 'components/data-visualization/Table/Header';
import Row from 'components/data-visualization/Table/Row';
import Cell from 'components/data-visualization/Table/Cell';
import { definitionValue } from 'styles/base-classes';
import modularScale from 'styles/helpers/modular-scale';
import { Link as RouterLink } from 'react-router-dom';
import { ReactComponent as Caret } from 'icons/icon-toggle-caret.svg';
import { primaryTypography } from 'styles/global/typography/typography';

interface IObject {
  [key: string]: any;
}

export const TableWrapper = styled('div')<IObject>`
  margin: ${({ isLargeScreen }): string =>
    isLargeScreen ? '0 -1.1em' : '0 -0.5em'};
`;

export const Table = styled(BaseTable)`
  ma thead th[data-hasdropdown='false'] div {
    justify-content: flex-start;
  }
`;

export const Header = styled(ColumnHeader)<IObject>`
  color: ${({ theme }): string => theme.CasperGrey};
  &,
  &:first-of-type {
    text-align: ${({ textAlign }): string => (textAlign ? textAlign : 'left')};
  }
`;

export const TableRow = styled(Row)<IObject>`
  display: flex;
  flex-flow: row wrap;
  padding-bottom: ${({ theme, isLargeScreen }): string =>
    isLargeScreen && theme.spacing.xsmall};
  border-bottom: 1px solid
    ${({ theme, isExpanded }): string =>
      isExpanded ? 'transparent' : theme.LightGrey};
  background: ${({ theme, isWmgRow, isExpanded }): string =>
    isExpanded ? theme.Sand : isWmgRow && theme.Blue + '0F'};

  &:hover {
    background: ${({ theme, isWmgRow }): string => isWmgRow && theme.LightGrey};
  }

  &:last-of-type {
    ${Cell} {
      border-bottom: none;
    }
  }

  @media screen and (min-width: 60em) {
    display: table-row;
    padding-bottom: 0;
    border-bottom: 0;

    td {
      border-bottom: 1px solid ${({ theme }): string => theme.LightGrey};
    }
  }
`;

export const TableCell = styled(Cell)<IObject>`
  flex: 0 0 10%;
  display: flex;
  align-items: ${({ isLargeScreen }): string =>
    isLargeScreen ? 'center' : 'start'};
  padding: 0;
  order: 4;

  > div {
    padding: ${({ theme }): string => theme.spacing.xsmall};
  }

  @media screen and (min-width: 60em) {
    display: table-cell;
    padding: ${({ theme }): string =>
      `${theme.spacing.small} ${theme.spacing.xsmall} ${theme.spacing.small}`};

    &:first-of-type {
      padding-left: ${({ theme }): string => theme.spacing.xsmall};
    }

    > div {
      padding: 0;
      display: block;
    }
  }
`;

const FixedWidthTableCell = styled(TableCell)`
  text-align: center;

  @media screen and (min-width: 60em) {
    width: 5em;
  }
`;

export const MobileFixedWidthTableCell = styled(FixedWidthTableCell)`
  flex: 0 0 10%;
  padding-top: 0.5em;
  padding-bottom: 0.5em;
  justify-content: center;

  @media screen and (min-width: 35em) {
    flex: 0 0 10%;
  }

  &:first-of-type {
    order: 1;
    /* justify-content: flex-start; */
    /* padding-left: ${({ theme }): string => theme.spacing.small}; */
  }

  &:last-of-type {
    order: 4;
    /* justify-content: flex-end; */
    /* padding-right: ${({ theme }): string => theme.spacing.small}; */
  }
`;

export const StyledCaret = styled(Caret)<IObject>`
  transition: ${({ theme }): string =>
    `transform ${theme.transitionDuration} ${theme.timingFunction}`};
  transform: ${({ open }): string => (open ? 'rotate(180deg)' : 'rotate(0)')};
`;

export const CellContent = styled('div')<IObject>`
  ${definitionValue};
  font-size: ${modularScale(-1)};
  font-weight: normal;
  display: flex;
  align-items: center;

  ${({ smallScreen }): any =>
    smallScreen &&
    css`
      min-height: 4.2em;
      display: flex;
      align-items: center;
    `};

  ${({ textAlign, noWrap, theme }): any => css`
    white-space: ${noWrap ? 'nowrap' : 'initial'};
    transition: ${`${theme.transitionDuration} ${theme.timingFunction} transform`};

    @media screen and (min-width: 60em) {
      text-align: ${textAlign ? textAlign : 'left'};
    }
  `}
`;

export const ProductTableCell = styled(TableCell)`
  flex: 1 0 70%;
`;

export const Position = styled('span')<IObject>`
  font-weight: 600;
  font-size: ${modularScale(2)};

  ${({ smallScreen }): any =>
    smallScreen &&
    css`
      min-height: 3em;
      display: flex;
      align-items: center;
    `};
`;

export const Link = styled(RouterLink)`
  color: inherit;
  text-decoration: none;
  border-bottom: 1px solid transparent;

  &:hover {
    border-bottom: 1px solid ${({ theme }): string => theme.DeepBlue};
  }
`;

export const EntityMetaData = styled('div')`
  ${primaryTypography};
  transition: ${({ theme }): string =>
    `${theme.transitionDuration} ${theme.timingFunction} transform`};
  font-weight: normal;
`;

export const EntityTitle = styled('h3')`
  font-weight: 600;
  margin: 0;
  font-size: ${modularScale(1)};
  word-break: break-all;
`;

export const EntityMeta = styled('p')`
  font-size: ${modularScale(0)};
  font-weight: normal;
  color: ${({ theme }): string => theme.CasperGrey};
  margin: 0.25em 0;
`;

export const ProductInfoBlock = styled('div')`
  margin: 0.25em 0;
`;

export const ProductAdditionalInfo = styled('span')`
  font-size: ${modularScale(0)};
  font-weight: normal;
  text-transform: uppercase;
  color: ${({ theme }): string => theme.CasperGrey};
`;

export const WmgLabel = styled('div')`
  display: flex;
  color: ${({ theme }): string => theme.CasperGrey};
  font-size: ${modularScale(-2)};
  font-weight: normal;
  align-items: center;
  margin-bottom: 2em;

  &:before {
    content: '';
    margin-right: 0.9em;
    width: 39px;
    height: 23px;
    background: ${({ theme }): string => theme.Blue + '0F'};
  }
`;

export const Layout = styled('div')`
  margin: 0 auto;
  background-color: ${({ theme }): string => theme.White};

  @media screen and (min-width: 60em) {
    max-width: calc(100% - (2em * 2));
  }

  @media screen and (min-width: 80em) {
    max-width: calc(100% - (8em * 2));
  }
`;

export const ChartTableWrapper = styled('section')`
  margin: 0 auto;
  background-color: ${({ theme }): string => theme.White};
  padding: ${({ theme }): string =>
    `${theme.spacing.large} ${theme.spacing.large} ${theme.spacing.small}`};

  @media screen and (min-width: 60em) {
    padding: ${({ theme }): string =>
      `${theme.spacing.large} ${theme.spacing.large} ${theme.spacing.small}`};
  }

  @media screen and (min-width: 80em) {
    padding: ${({ theme }): string =>
      `${theme.spacing.xlarge} ${theme.spacing.xlarge} ${theme.spacing.small}`};
  }
`;

export const WmgPercent = styled('span')`
  color: ${({ theme }): string => theme.Blue};
  font-weight: 600;
`;

export const PaginationWrapper = styled('div')`
  padding: ${({ theme }): string =>
    `0 ${theme.spacing.large} ${theme.spacing.small}`};

  @media screen and (min-width: 60em) {
    padding: ${({ theme }): string =>
      `0 ${theme.spacing.large} ${theme.spacing.small}`};
  }

  @media screen and (min-width: 80em) {
    padding: ${({ theme }): string =>
      `0 ${theme.spacing.xlarge} ${theme.spacing.small}`};
  }
`;

export const ComponentFilters = styled('div')`
  margin: ${({ theme }): string =>
    `${theme.spacing.xxsmall} 0 ${theme.spacing.small} 0`};
  color: ${({ theme }): string => theme.DeepBlue};
  font-size: ${modularScale(-1)};
`;

export const ToolbarMenu = styled('div')`
  margin-left: auto;
  flex-shrink: 0;
  transform: translateY(
    -0.256em
  ); /** This value is $(modularScale(5) / 2) - 1.5em (Half the height of the Toolbar Button) */
`;

export const Toolbar = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  position: relative;
  z-index: 1; /* Make sure we are higher than the dropdown and toggles that could be nested in this component */

  h1 {
    word-break: break-word;
  }
`;
